import { logError } from '@/logger'
import { analytics } from '@/plugins/analytics'
import { isMobileDevice } from '@/utils/mobileDetection'

import type { HermesGuardFn } from './types'

/**
 * Router guard that redirects mobile users to the get app prompt page
 *
 * Checks sessionStorage for dismissal - persists across navigation but clears on new session
 * @param to - The route location object
 * @returns The redirect location object or undefined if no redirect is needed
 */
export const checkGetAppPrompt: HermesGuardFn = async (to) => {
  const { name } = to

  // Skip if already on get app prompt page
  if (name === 'auth.get-app-prompt') {
    return undefined
  }

  // Skip if user has dismissed the prompt during this session
  if (typeof sessionStorage !== 'undefined') {
    try {
      if (sessionStorage.getItem('skipGetAppPrompt') === 'true') {
        return undefined
      }
    } catch (error) {
      logError({
        context: 'checkGetAppPrompt.sessionStorageCheck',
        error,
        message: 'Failed to check sessionStorage in router guard',
      })
      // Continue with guard logic - show prompt as safe default
    }
  }

  // Check if mobile device
  if (!isMobileDevice()) {
    return undefined
  }

  // Track that we're redirecting user to get-app prompt
  // Only track when we're actually going to redirect
  analytics.track('Get App Prompt Redirect', {
    fromRoute: name || 'unknown',
    originalDestination: to.fullPath,
  })

  // Redirect to get app prompt page
  return {
    name: 'auth.get-app-prompt',
    query: { redirect: to.fullPath }, // Preserve intended destination
  }
}
